import React, { useState } from 'react';
import { Play, Pause, Maximize, Volume2 } from 'lucide-react';
import { showreelVideo } from '../data/mock';

const ShowreelSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  const handlePlayClick = () => {
    setShowPlayer(true);
    setIsPlaying(true);
  };

  return (
    <section id="showreel" className="relative bg-black py-20 lg:py-32 overflow-hidden">
      {/* Futuristic Background */}
      <div className="absolute inset-0 futuristic-grid opacity-10" />
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-red-600 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: Math.random() * 0.5 + 0.3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 text-3d">
            Our{' '}
            <span className="holographic animate-gradient">Showreel</span>
          </h2>
          <div className="relative inline-block">
            <p className="text-lg md:text-xl text-white/70 max-w-2xl">
              Experience our cinematic journey through award-winning commercials and compelling documentaries.
            </p>
            <div className="absolute -bottom-2 left-0 right-0 h-[2px] holographic" />
          </div>
        </div>

        {/* Video Player */}
        <div className="max-w-6xl mx-auto">
          <div className="relative transform-3d group">
            {/* Main Video Container */}
            <div className="relative aspect-video rounded-3xl overflow-hidden glass-card border-2 border-white/10 shadow-premium">
              {!showPlayer ? (
                // Thumbnail with Play Button
                <div className="relative w-full h-full group cursor-pointer" onClick={handlePlayClick}>
                  <img
                    src={showreelVideo.thumbnail}
                    alt="Showreel thumbnail"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  
                  {/* Holographic Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-red-600/20 to-black/60 group-hover:from-black/40 group-hover:via-red-600/30 group-hover:to-black/40 transition-all duration-300" />
                  
                  {/* Scan Lines */}
                  <div className="absolute inset-0 scanlines opacity-50" />
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <div className="glass-ultra p-10 rounded-full transform group-hover:scale-110 transition-all duration-300 neon-red">
                        <Play size={60} className="text-red-600 fill-red-600 ml-2" />
                      </div>
                      <div className="absolute inset-0 bg-red-600 rounded-full blur-3xl opacity-50 group-hover:opacity-75 animate-pulse-glow" />
                    </div>
                  </div>

                  {/* Duration Badge */}
                  <div className="absolute bottom-6 right-6 glass-ultra px-4 py-2 rounded-full border border-white/20">
                    <span className="text-white text-sm font-bold">{showreelVideo.duration}</span>
                  </div>

                  {/* Holographic Corner Accents */}
                  <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-red-600 rounded-tl-3xl opacity-50" />
                  <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-red-600 rounded-br-3xl opacity-50" />
                </div>
              ) : (
                // Mock Video Player
                <div className="relative w-full h-full bg-black">
                  <img
                    src={showreelVideo.thumbnail}
                    alt="Video playing"
                    className="w-full h-full object-cover opacity-90"
                  />
                  
                  {/* Playing Indicator */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="glass-ultra px-8 py-4 rounded-full text-white font-medium neon-red">
                      <span className="holographic">Now Playing - {showreelVideo.title}</span>
                    </div>
                  </div>

                  {/* Futuristic Controls */}
                  <div className="absolute bottom-0 left-0 right-0 glass-ultra border-t border-white/10 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-6">
                        <button 
                          onClick={() => setIsPlaying(!isPlaying)}
                          className="glass-ultra p-3 rounded-full hover:bg-red-600/20 transition-all duration-300 neon-red"
                        >
                          {isPlaying ? <Pause size={24} className="text-white" /> : <Play size={24} className="text-white" />}
                        </button>
                        <button className="glass-ultra p-3 rounded-full hover:bg-white/20 transition-all duration-300">
                          <Volume2 size={20} className="text-white" />
                        </button>
                        <span className="text-white text-sm font-mono">0:45 / {showreelVideo.duration}</span>
                      </div>
                      <button className="glass-ultra p-3 rounded-full hover:bg-white/20 transition-all duration-300">
                        <Maximize size={20} className="text-white" />
                      </button>
                    </div>
                    {/* Progress Bar */}
                    <div className="relative w-full h-2 glass-ultra rounded-full overflow-hidden">
                      <div className="absolute inset-0 holographic w-1/4 animate-pulse-glow" />
                    </div>
                  </div>

                  {/* Scan Lines */}
                  <div className="absolute inset-0 scanlines opacity-30" />
                </div>
              )}

              {/* Animated Border */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 rounded-3xl border-2 border-red-600/50 animate-spin-slow" />
              </div>
            </div>

            {/* Decorative 3D Elements */}
            <div className="absolute -top-8 -right-8 w-16 h-16 glass-ultra rounded-2xl border border-red-600/30 transform rotate-12 group-hover:rotate-45 transition-transform duration-700" />
            <div className="absolute -bottom-8 -left-8 w-16 h-16 glass-ultra rounded-2xl border border-white/30 transform -rotate-12 group-hover:-rotate-45 transition-transform duration-700" />
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <p className="text-white/70 mb-6 text-lg">
              Ready to create something extraordinary together?
            </p>
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="group relative bg-gradient-to-r from-red-600 to-red-500 text-white px-10 py-5 rounded-full overflow-hidden transition-all duration-300 text-base font-semibold tracking-wide uppercase neon-red hover:scale-110 hover:-translate-y-1"
            >
              <span className="relative z-10">Start Your Project</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
            </button>
          </div>
        </div>
      </div>

      {/* Decorative Orbs */}
      <div className="absolute top-1/2 -left-40 w-80 h-80 bg-red-600 rounded-full blur-[120px] opacity-10 animate-pulse-glow" />
      <div className="absolute top-1/2 -right-40 w-80 h-80 bg-white rounded-full blur-[120px] opacity-5 animate-pulse-glow" />
    </section>
  );
};

export default ShowreelSection;
